import { Portal, Select, createListCollection } from '@ark-ui/react'
import { useMemo } from 'react'

/**
 * Which book's prices the record is built from.
 *
 * **Why not the native `<select>` it replaces.** In production this list runs to ninety-seven
 * entries of raw provider slugs — `unibet.se`, `betfair-ex`, `pokerstars` — and a native dropdown of
 * that length is a scroll with no structure: no grouping, no coverage shown next to the name, and on
 * a phone an OS wheel that renders the option text at whatever size it likes. Ark's select gives a
 * listbox we can lay out, so each row carries the book and how many fixtures it actually priced,
 * which is the number that decides whether a book is worth looking at at all.
 *
 * The order comes from the server and is deliberate: books a reader here could plausibly hold an
 * account with come first, and coverage breaks ties. Re-sorting alphabetically in the client would
 * undo that and put a grey-market book at the top, which is what it used to do.
 */
export function BookmakerPicker({
  books,
  selected,
  onSelect,
}: {
  books: { book: string; name: string; fixtures: number }[]
  selected: string
  onSelect: (book: string) => void
}) {
  const collection = useMemo(
    () =>
      createListCollection({
        items: books,
        // Value is the slug, because that is what the filter round-trips to the API; the label
        // is the operator's own name, because that is what a reader recognises.
        itemToValue: (item) => item.book,
        itemToString: (item) => item.name,
      }),
    [books],
  )

  return (
    <Select.Root
      collection={collection}
      value={[selected]}
      onValueChange={(details) => details.value[0] && onSelect(details.value[0])}
      positioning={{ sameWidth: false, gutter: 4 }}
      className="flex items-center gap-2"
    >
      <Select.Label className="shrink-0 text-sm text-ink-soft">Prices from</Select.Label>
      <Select.Control>
        <Select.Trigger className="flex min-h-11 items-center gap-2 rounded border border-line bg-surface px-2.5 py-1 text-sm text-ink hover:border-line-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:min-h-0">
          <Select.ValueText />
          <Select.Indicator className="text-ink-faint">▾</Select.Indicator>
        </Select.Trigger>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content className="z-50 max-h-80 min-w-56 overflow-y-auto overscroll-contain rounded-lg border border-line bg-surface-overlay p-1 shadow-overlay focus-visible:outline-none">
            {books.map((entry) => (
              <Select.Item
                key={entry.book}
                item={entry}
                className="flex cursor-pointer items-baseline justify-between gap-4 rounded px-2 py-1.5 text-sm text-ink-soft data-[highlighted]:bg-surface-raised data-[highlighted]:text-ink data-[state=checked]:text-accent"
              >
                <Select.ItemText>{entry.name}</Select.ItemText>
                {/* Coverage beside the name, because a book that priced two fixtures and one that
                    priced sixty are not the same choice and the name does not say which is which. */}
                <span className="tabular shrink-0 text-xs text-ink-faint">
                  {entry.fixtures} priced
                </span>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
      <Select.HiddenSelect />
    </Select.Root>
  )
}
